import { useState } from 'react';
import { Button, ButtonDropdown, Dialog, Spinner } from '@rio-cloud/rio-uikit';
import { useConsultants } from '../../../../hooks/useConsultants';
import ConsultantForm from '../form/ConsultantForm';
import ConsultantActivityDialog from '../activity/ConsultantActivityDialog';
import { RestrictArea } from '../../../../components/restrictArea';
import { Consultant } from '../../../../models/consultant';
import { useIntl } from 'react-intl';

const ConsultantListContent = () => {
  const intl = useIntl();
  const {
    consultants,
    loading,
    loadingMore,
    deleteLoading,
    error,
    showForm,
    editingConsultant,
    showDeleteDialog,
    consultantToDelete,
    handleEdit,
    handleDelete,
    confirmDelete,
    handleFormClose,
    handleFormSuccess,
    setShowForm,
    setShowDeleteDialog,
    editConsultant,
    addConsultant,
    userHasAccess,
    scrollContainerRef,
    isDragging,
    handleMouseDown,
    handleMouseLeave,
    handleMouseUp,
    handleMouseMove,
    hasMoreData,
    loadMoreConsultants,
    searchTerm,
    setSearchTerm,
    refreshing,
  } = useConsultants();

  const [activityConsultant, setActivityConsultant] = useState<Consultant | null>(null);

  if (loading || userHasAccess === null) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner />
      </div>
    );
  }

  if (!userHasAccess) {
    return <RestrictArea showBackButton={false} />;
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-red-600 text-center">
          <p className="text-lg font-semibold mb-2">{intl.formatMessage({ id: 'intl-msg:backoffice.consultantList.loadError' })}</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const consultantFeatures = (consultant: Consultant): string => {
    let status = ""
    if (consultant.groupConsultant) status = concatPipe(status, intl.formatMessage({ id: 'intl-msg:backoffice.consultantList.featureGroup' }));
    if (consultant.specialist) status = concatPipe(status, intl.formatMessage({ id: 'intl-msg:backoffice.consultantList.featureSpecialist' }));
    if (consultant.sendEmail) status = concatPipe(status, intl.formatMessage({ id: 'intl-msg:backoffice.consultantList.featureEmail' }));
    return status
  }

  const concatPipe = (value: string, newValue: string): string => {
    if (value!) {
      value += " | "
    }
    return value += newValue
  }

  // Função auxiliar para impedir que o clique no texto inicie o drag da tabela
  const preventDragOnText = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div className="p-6 max-w-[1000px] mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <div className="text-size-h2">{intl.formatMessage({ id: 'intl-msg:backoffice.consultantList.title' })}</div>
        </div>
        <Button
          bsStyle={Button.PRIMARY}
          onClick={() => setShowForm(true)}
          iconName="rioglyph-plus-sign"
        >
          {intl.formatMessage({ id: 'intl-msg:backoffice.consultantList.newConsultant' })}
        </Button>
      </div>

      <div className="mb-4">
        <div className="input-group">
          <span className="input-group-addon">
            {refreshing ? (
              <span className="rioglyph rioglyph-spinner spinning"></span>
            ) : (
              <span className="rioglyph rioglyph-search"></span>
            )}
          </span>
          <input
            type="text"
            className="form-control"
            placeholder={intl.formatMessage({ id: 'intl-msg:backoffice.consultantList.searchPlaceholder' })}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div
        className={`bg-white dark:bg-neutral-800 rounded-lg shadow overflow-x-auto ${isDragging ? 'cursor-grabbing select-none' : 'cursor-grab'}`}
        ref={scrollContainerRef}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseLeave}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
      >
        <table className={`w-full divide-y divide-gray-200 dark:divide-neutral-700 m-6 max-w-[940px] ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}>
          <thead className="bg-gray-50 dark:bg-neutral-900">
            <tr>
              <th className="px-6 py-3 text-left font-medium text-gray-500 dark:text-neutral-400 cursor-default">
                {intl.formatMessage({ id: 'intl-msg:backoffice.consultantList.name' })}
              </th>
              <th className="px-6 py-3 text-left font-medium text-gray-500 dark:text-neutral-400 cursor-default">
                {intl.formatMessage({ id: 'intl-msg:backoffice.consultantList.email' })}
              </th>
              <th className="px-6 py-3 text-left font-medium text-gray-500 dark:text-neutral-400 cursor-default">
                {intl.formatMessage({ id: 'intl-msg:backoffice.consultantList.phone' })}
              </th>
              <th className="px-6 py-3 text-left font-medium text-gray-500 dark:text-neutral-400 cursor-default">
                DN
              </th>
              <th className="px-6 py-3 text-left font-medium text-gray-500 dark:text-neutral-400 cursor-default"></th>
              <th className="px-6 py-3 text-right font-medium text-gray-500 dark:text-neutral-400 sticky right-0 top-0 z-10 bg-gray-50 dark:bg-neutral-900 cursor-default"></th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-neutral-800 divide-y divide-gray-200 dark:divide-neutral-700">
            {consultants.map((consultant) => (
              <tr key={consultant.id} className="hover:bg-gray-50 dark:hover:bg-neutral-700">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div 
                    className="font-medium text-gray-900 dark:text-neutral-100 cursor-text inline-block"
                    onMouseDown={preventDragOnText}
                  >
                    {consultant.firstName} {consultant.lastName}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div 
                    className="text-gray-900 dark:text-neutral-100 cursor-text inline-block"
                    onMouseDown={preventDragOnText}
                  >
                    {consultant.email}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div 
                    className="text-gray-900 dark:text-neutral-100 cursor-text inline-block"
                    onMouseDown={preventDragOnText}
                  >
                    {consultant.phoneNumber}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div 
                    className="text-gray-900 dark:text-neutral-100 cursor-text inline-block"
                    onMouseDown={preventDragOnText}
                  >
                    {consultant.dn}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div 
                    className="text-gray-900 dark:text-neutral-100 cursor-text inline-block"
                    onMouseDown={preventDragOnText}
                  >
                    {consultantFeatures(consultant)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right font-medium sticky right-0 top-0 z-10 bg-white dark:bg-neutral-800 cursor-default">
                  <div className="flex justify-end space-x-2">
                    <ButtonDropdown
                      title={<span className='rioglyph rioglyph-option-vertical' />}
                      items={[
                        {
                          value: intl.formatMessage({ id: 'intl-msg:backoffice.consultantList.edit' }),
                          onSelect: () => handleEdit(consultant)
                        },
                        {
                          value: intl.formatMessage({ id: 'intl-msg:backoffice.activity.menuItem' }),
                          onSelect: () => setActivityConsultant(consultant)
                        },
                        {
                          value: intl.formatMessage({ id: 'intl-msg:backoffice.consultantList.delete' }),
                          onSelect: () => handleDelete(consultant)
                        }
                      ]}
                      bsStyle='muted'
                      pullRight
                      noCaret
                      usePortal
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {consultants.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-500 dark:text-neutral-400">
              <p className="font-medium mb-2">{intl.formatMessage({ id: 'intl-msg:backoffice.consultantList.empty' })}</p>
            </div>
          </div>
        )}
      </div>

      {consultants.length > 0 && (
        <div className="LoadMoreButton align-items-center display-flex justify-content-center margin-top-25 margin-bottom-25 non-printable">
          <div className="min-width-150">
            <div className="text-center margin-bottom-3">
              <span className="text-medium">{consultants.length}</span>
            </div>
            <div className="display-flex justify-content-center">
              {hasMoreData ? (
                <button
                  className="btn btn-link padding-right-20 margin-top--5"
                  type="button"
                  onClick={loadMoreConsultants}
                  disabled={loadingMore}
                >
                  <span className={`rioglyph ${loadingMore ? 'rioglyph-spinner spinning' : 'rioglyph-arrow-down'}`}></span>
                  {loadingMore ? intl.formatMessage({ id: 'intl-msg:backoffice.consultantList.loading' }) : intl.formatMessage({ id: 'intl-msg:backoffice.consultantList.loadMore' })}
                </button>
              ) : (
                <span className="padding-top-4 padding-bottom-10">{intl.formatMessage({ id: 'intl-msg:backoffice.consultantList.allLoaded' })}</span>
              )}
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <Dialog
          show={showForm}
          onClose={handleFormClose}
          showCloseButton={true}
          title={editingConsultant ? intl.formatMessage({ id: 'intl-msg:backoffice.consultantList.editConsultant' }) : intl.formatMessage({ id: 'intl-msg:backoffice.consultantList.newConsultant' })}
          bsSize={Dialog.SIZE_SM}
          body={(
            <ConsultantForm
              consultant={editingConsultant}
              editConsultant={editConsultant}
              addConsultant={addConsultant}
              onSuccess={() => {
                handleFormSuccess();
              }}
              onCancel={() => {
                handleFormClose();
              }}
            />
          )}
        />
      )}

      {activityConsultant && (
        <ConsultantActivityDialog
          consultant={activityConsultant}
          onClose={() => setActivityConsultant(null)}
        />
      )}

      <Dialog
        show={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        title={intl.formatMessage({ id: 'intl-msg:backoffice.consultantList.confirmDeleteTitle' })}
        bsSize={Dialog.SIZE_SM}
      >
        <div className="p-6">
          <p className="text-size-h4 text-gray-700 dark:text-neutral-300 mb-4">
            {intl.formatMessage(
              { id: 'intl-msg:backoffice.consultantList.confirmDeleteMessage' },
              {
                name: `${consultantToDelete?.firstName} ${consultantToDelete?.lastName}`,
                strong: (chunks) => <strong>{chunks}</strong>,
              }
            )}
          </p>
          <p className="text-gray-600 dark:text-neutral-400 mb-6">
            {intl.formatMessage({ id: 'intl-msg:backoffice.consultantList.cannotBeUndone' })}
          </p>
          <div className="flex justify-end space-x-3">
            <Button
              bsStyle={Button.DEFAULT}
              onClick={() => setShowDeleteDialog(false)}
            >
              {intl.formatMessage({ id: 'intl-msg:backoffice.consultantList.cancel' })}
            </Button>
            <Button
              bsStyle={Button.DANGER}
              onClick={confirmDelete}
              disabled={deleteLoading}
              iconName={deleteLoading ? "rioglyph-spinner" : undefined}
            >
              {deleteLoading ? intl.formatMessage({ id: 'intl-msg:backoffice.consultantList.deleting' }) : intl.formatMessage({ id: 'intl-msg:backoffice.consultantList.delete' })}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

const ConsultantList = (props: any) => {
  return (
    <ConsultantListContent {...props} />
  );
};

export default ConsultantList;
